import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { isLimitError } from '../../api/api';
import Alert from '../../components/Alert';
import Icon from '../../components/Icon';
import PublishModal from '../../components/PublishModal';
import { useCreateContext } from '../../product/CreateContext';
import { storage } from '../../product/productStorage';
import { addDays } from '../../product/productUtils';
import { ProductTile } from '../../shell/Sidebar';
import { PUBLISH_PLATFORMS, formatWhen, platformIcon, platformIdFor, platformLabel } from '../../utils/publishing';
import { WEEK_DAYS } from '../../utils/planInsights';
import { upsertPlanPostEntry } from '../../utils/reportEntries';
import { CopyButton, Field } from './generatorUi';
import { scriptAsText, useCreateFlow, usePrefill } from './flow';

// What each platform takes in one post (caption, link and hashtags together)
const TEXT_LIMITS = { x: 280, threads: 500, pinterest: 500, linkedin: 3000, instagram: 2200, tiktok: 2200, youtube: 5000, facebook: 63206 };
// The plan keeps this much of a draft's caption
const PLAN_CAPTION_LIMIT = 5000;
const META_LIMIT = 155;

const toText = (value) => (typeof value === 'string' ? value : '');
const isHttpUrl = (value) => /^https?:\/\/\S+$/i.test(value || '');

const toHashtags = (value) => {
    const tags = toText(value).split(/[\s,]+/).map(tag => tag.replace(/^#+/, '').trim()).filter(Boolean).map(tag => `#${tag}`);
    return tags.filter((tag, index) => tags.findIndex(other => other.toLowerCase() === tag.toLowerCase()) === index);
};

// Hook, caption and hashtags as one post; a caption that already opens with the hook keeps it once
const composePost = ({ hook, caption, hashtags, link }) => {
    const opener = hook.trim();
    const body = caption.trim();
    return [
        opener && !body.toLowerCase().startsWith(opener.toLowerCase()) ? opener : '',
        body,
        link,
        hashtags.join(' '),
    ].filter(Boolean).join('\n\n');
};

// Markdown to a plain-text excerpt for the article preview
const excerptOf = (markdown, length = 320) => {
    const plain = toText(markdown)
        .replace(/^#{1,6}\s+.*$/gm, '')
        .replace(/[*_`>]/g, '')
        .replace(/^\s*[-+]\s+/gm, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        // List items and short lines read as sentences
        .map(line => (/[.!?:…]$/.test(line) ? line : `${line}.`))
        .join(' ');
    return plain.length > length ? `${plain.slice(0, length).replace(/\s+\S*$/, '')}…` : plain;
};

const readMinutes = (markdown) => Math.max(1, Math.round(toText(markdown).split(/\s+/).filter(Boolean).length / 230));

const extensionOf = (src) => (src.match(/^data:image\/(\w+)/)?.[1] || 'png').replace('jpeg', 'jpg');

// The generated media as a file the Publish modal can upload
const mediaFiles = async (flow) => {
    const src = flow.video || flow.image;
    if (!src) return [];
    const blob = await (await fetch(src)).blob();
    if (flow.video) return [new File([blob], 'video.mp4', { type: blob.type && blob.type.startsWith('video/') ? blob.type : 'video/mp4' })];
    const type = blob.type || 'image/png';
    return [new File([blob], `image.${type.split('/')[1] === 'jpeg' ? 'jpg' : type.split('/')[1] || 'png'}`, { type })];
};

const readLinkDestination = () => {
    const prefs = storage.getJson('planPreferences', {});
    return prefs && isHttpUrl(toText(prefs.linkDestination).trim()) ? prefs.linkDestination.trim() : '';
};

const errorText = (error, fallback) => {
    const message = error?.response?.data?.error;
    return typeof message === 'string' && message.trim() && error.response.status < 500 ? message : fallback;
};

const ReviewStep = () => {
    const navigate = useNavigate();
    const { product, seed } = useCreateContext();
    const { flow, update, report, kind, steps, goTo, startOver } = useCreateFlow();
    const isPost = kind === 'social' || kind === 'video';
    const planPost = seed?.planWeek && seed?.postId ? {
        id: toText(seed.postId),
        platform: toText(seed.platform),
        day: toText(seed.day),
        time: toText(seed.time),
        theme: toText(seed.theme),
        hook: flow.hook,
        shortUrl: toText(seed.shortUrl),
        draft: null,
    } : null;

    // The platform the post is sized for: the caption's, else the planned post's
    const [platform, setPlatform] = useState(() => (
        PUBLISH_PLATFORMS[flow.captionPlatform] ? flow.captionPlatform : platformIdFor(seed?.platform) || (kind === 'video' ? 'tiktok' : 'instagram')
    ));
    usePrefill(PUBLISH_PLATFORMS[flow.captionPlatform] ? flow.captionPlatform : '', platform, setPlatform);

    // Hashtags are typed as text; the flow keeps them as a list
    const [tagsText, setTagsText] = useState(() => flow.hashtags.join(' '));
    useEffect(() => {
        setTagsText(current => (toHashtags(current).join(' ') === flow.hashtags.join(' ') ? current : flow.hashtags.join(' ')));
    }, [flow.hashtags]);

    const [saving, setSaving] = useState(false);
    const [savedAs, setSavedAs] = useState(''); // the draft last saved to the plan, as JSON
    const [marking, setMarking] = useState(false);
    const [posted, setPosted] = useState(null); // { shortUrl } once marked as posted
    const [preparing, setPreparing] = useState(false);
    const [publish, setPublish] = useState(null); // { files } while the modal is open
    const [status, setStatus] = useState(null); // { severity, text }
    const [copied, setCopied] = useState(false);
    const statusRef = useRef(null);

    const article = flow.article;
    const ad = flow.ad;

    /* ---------- What the post is ---------- */

    const postText = composePost({ hook: flow.hook, caption: flow.caption, hashtags: flow.hashtags, link: platform !== 'instagram' && platform !== 'tiktok' ? flow.link : '' });
    const articleText = article ? `# ${article.title}\n\n${article.body}` : '';
    const adFields = ad ? (ad.fields.length ? ad.fields : ['headline', 'primaryText', 'description']) : [];
    const adText = ad ? [...adFields.map(field => ad[field]), ad.cta].map(item => toText(item).trim()).filter(Boolean).join('\n\n') : '';
    const copyText = kind === 'blog' ? articleText : kind === 'ads' ? adText : postText;
    const limit = TEXT_LIMITS[platform] || 2200;

    const draft = (() => {
        if (kind === 'blog') return article ? { hook: article.title.trim(), caption: article.body.trim(), hashtags: [] } : null;
        if (kind === 'ads') {
            return ad ? {
                hook: (ad.headline || ad.primaryText).trim(),
                caption: [ad.primaryText, ad.description, ad.cta && `CTA: ${ad.cta}`].map(item => toText(item).trim()).filter(Boolean).join('\n\n'),
                hashtags: [],
            } : null;
        }
        return flow.hook.trim() || flow.caption.trim() ? { hook: flow.hook.trim(), caption: flow.caption.trim(), hashtags: flow.hashtags } : null;
    })();
    const isSaved = Boolean(draft) && savedAs === JSON.stringify(draft);

    const mediaSrc = flow.video || flow.image;
    const mediaStep = steps.find(step => step.id === 'video' || step.id === 'image');
    const missing = steps.filter(step => step.id !== 'review' && !step.optional && !flow.done[step.id]);

    const showStatus = (next) => {
        setStatus(next);
        window.requestAnimationFrame(() => statusRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    };

    /* ---------- Actions ---------- */

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(copyText);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            showStatus({ severity: 'warning', text: "Couldn't copy here. Select the text in the preview and copy it by hand." });
        }
    };

    const saveToPlan = async () => {
        if (!planPost || !draft || saving) return;
        setSaving(true);
        setStatus(null);
        try {
            await api.updatePlanPost(seed.planWeek, planPost.id, { draft });
            setSavedAs(JSON.stringify(draft));
            report('review', {}, true);
            showStatus({
                severity: 'success',
                text: draft.caption.length > PLAN_CAPTION_LIMIT
                    ? `Saved to your plan. The plan keeps the first ${PLAN_CAPTION_LIMIT.toLocaleString()} characters; copy the full text from here.`
                    : 'Saved to your plan. The post’s draft now has this hook and text.',
            });
        } catch (e) {
            if (isLimitError(e)) return;
            showStatus({ severity: 'error', text: errorText(e, "Couldn't save to your plan. Your post is still here, so please try again.") });
        } finally {
            setSaving(false);
        }
    };

    const markPosted = async () => {
        if (!planPost || marking) return;
        setMarking(true);
        setStatus(null);
        try {
            const plan = await api.publishPlanPost(seed.planWeek, planPost.id, readLinkDestination() || undefined);
            const updated = (Array.isArray(plan?.posts) ? plan.posts : []).find(item => item && item.id === planPost.id) || {};
            const shortUrl = toText(updated.shortUrl);
            const dayIndex = Math.max(0, WEEK_DAYS.findIndex(day => day.toLowerCase() === planPost.day.toLowerCase()));
            upsertPlanPostEntry({ ...planPost, ...updated, id: planPost.id, platform: planPost.platform, theme: planPost.theme || flow.hook }, addDays(seed.planWeek, dayIndex), {
                linkSlug: toText(updated.linkSlug),
            });
            setPosted({ shortUrl });
            report('review', {}, true);
            showStatus({ severity: 'success', text: shortUrl ? `Marked as posted. Its tracked link is ${shortUrl}` : 'Marked as posted. Add its results on the Content plan later.' });
        } catch (e) {
            if (isLimitError(e)) return;
            showStatus({ severity: 'error', text: errorText(e, "Couldn't mark the post as posted. Please try again.") });
        } finally {
            setMarking(false);
        }
    };

    const openPublish = async () => {
        setPreparing(true);
        setStatus(null);
        let files = [];
        try {
            files = await mediaFiles(flow);
        } catch (e) {
            // The media can still be added in the modal
        }
        setPreparing(false);
        setPublish({ files });
    };

    const handlePublished = (created, { scheduledAt, warnings = [] } = {}) => {
        setPublish(null);
        report('review', {}, true);
        const accounts = created.length;
        const where = accounts ? ` to ${accounts === 1 ? '1 account' : `${accounts} accounts`}` : '';
        showStatus({
            severity: warnings.length ? 'warning' : 'success',
            text: [
                scheduledAt ? `Scheduled${where} for ${formatWhen(scheduledAt)}.` : `Publishing${where} now. It shows on Publishing in a moment.`,
                ...warnings,
            ].join(' '),
        });
    };

    const backToPlan = () => navigate(`/dashboard/plan?week=${encodeURIComponent(seed.planWeek)}`);

    /* ---------- Editing ---------- */

    const updateArticle = (change) => report('article', { article: { ...article, ...change } }, Boolean(article.body.trim()));
    const updateAd = (change) => update({ ad: { ...ad, ...change } });

    const renderEditor = () => {
        if (kind === 'blog') {
            if (!article) return null;
            return (
                <>
                    <Field label="Title">
                        <input className="ls-input" value={article.title} onChange={(e) => updateArticle({ title: e.target.value })} />
                    </Field>
                    <Field
                        label="Meta description"
                        hint={<span className={article.metaDescription.length > META_LIMIT ? 'ls-cflow-over' : undefined}>{article.metaDescription.length} / {META_LIMIT} characters</span>}
                    >
                        <textarea className="ls-textarea is-compact" rows={2} value={article.metaDescription} onChange={(e) => updateArticle({ metaDescription: e.target.value })} />
                    </Field>
                    <div className="ls-cflow-inline">
                        <span className="ls-hint">{article.body.split(/\s+/).filter(Boolean).length.toLocaleString()} words · about {readMinutes(article.body)} min read</span>
                        <button type="button" className="ls-link" onClick={() => goTo('article', { focus: true })}>Edit the article</button>
                    </div>
                </>
            );
        }
        if (kind === 'ads') {
            if (!ad) return null;
            const labels = { headline: 'Headline', primaryText: 'Primary text', description: 'Description' };
            return (
                <>
                    <div className="ls-hint">{ad.placementLabel}</div>
                    {adFields.map(field => {
                        const value = toText(ad[field]);
                        const max = ad.limits[field];
                        return (
                            <Field
                                key={field}
                                label={labels[field] || field}
                                hint={max ? <span className={value.length > max ? 'ls-cflow-over' : undefined}>{value.length} / {max}</span> : undefined}
                            >
                                <textarea
                                    className="ls-textarea is-compact"
                                    rows={field === 'primaryText' ? 3 : 1}
                                    value={value}
                                    onChange={(e) => updateAd({ [field]: e.target.value })}
                                />
                            </Field>
                        );
                    })}
                    <Field label="Call to action">
                        <input className="ls-input" value={toText(ad.cta)} onChange={(e) => updateAd({ cta: e.target.value })} placeholder="e.g. Learn more" />
                    </Field>
                </>
            );
        }
        return (
            <>
                <Field label="Platform">
                    <select className="ls-select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                        {Object.keys(PUBLISH_PLATFORMS).map(id => <option key={id} value={id}>{platformLabel(id)}</option>)}
                    </select>
                </Field>
                <Field label="Hook" hint="The first line people see">
                    <input className="ls-input" value={flow.hook} onChange={(e) => report('hook', { hook: e.target.value }, Boolean(e.target.value.trim()))} placeholder="Pick one in the Hook step, or write it here" />
                </Field>
                <Field label="Caption">
                    <textarea
                        className="ls-textarea"
                        rows={6}
                        value={flow.caption}
                        onChange={(e) => report('caption', { caption: e.target.value }, Boolean(e.target.value.trim() || flow.hashtags.length))}
                        placeholder="Write it in the Caption step, or here"
                    />
                </Field>
                <Field label="Hashtags" hint="Separate them with spaces">
                    <input
                        className="ls-input"
                        value={tagsText}
                        onChange={(e) => {
                            setTagsText(e.target.value);
                            const tags = toHashtags(e.target.value);
                            report('caption', { hashtags: tags }, Boolean(flow.caption.trim() || tags.length));
                        }}
                        placeholder="#mealprep #nightshift"
                    />
                </Field>
                {kind === 'video' && flow.script && (
                    <details className="ls-cflow-script">
                        <summary>
                            <span>Script · {flow.script.beats.length} beats</span>
                        </summary>
                        <div className="ls-cflow-script-body">
                            <pre>{scriptAsText(flow.script)}</pre>
                            <CopyButton text={scriptAsText(flow.script)} label="Copy script" />
                        </div>
                    </details>
                )}
            </>
        );
    };

    const renderMedia = (className = '') => {
        if (!mediaSrc) return null;
        return (
            <div className={`ls-media${flow.video ? ' is-video' : ''} ls-cflow-post-media ${className}`}>
                {flow.video
                    ? <video src={flow.video} controls muted playsInline />
                    : <img src={flow.image} alt={flow.imagePrompt || 'Post image'} />}
            </div>
        );
    };

    const renderPreview = () => {
        const head = (sub) => (
            <div className="ls-cflow-post-head">
                <ProductTile product={product} size={36} radius={18} fontSize={14} />
                <div className="ls-cflow-post-who">
                    <span className="ls-cflow-post-name">{product?.name || 'Your brand'}</span>
                    <span className="ls-cflow-post-sub">{sub}</span>
                </div>
                {isPost && <Icon name={platformIcon(platform)} size={20} className="ls-cflow-post-platform" />}
            </div>
        );

        if (kind === 'blog') {
            return (
                <article className="ls-cflow-post is-article">
                    {renderMedia('is-cover')}
                    <div className="ls-cflow-post-body">
                        <h3 className="ls-cflow-article-title">{article?.title || 'Your article title'}</h3>
                        {article?.metaDescription && <p className="ls-cflow-article-meta">{article.metaDescription}</p>}
                        <p className="ls-cflow-post-text">{article ? excerptOf(article.body) : 'Write the article to see it here.'}</p>
                        {head(article ? `${readMinutes(article.body)} min read` : 'Blog')}
                    </div>
                </article>
            );
        }

        if (kind === 'ads') {
            return (
                <article className="ls-cflow-post">
                    {head('Sponsored')}
                    <p className="ls-cflow-post-text">{ad?.primaryText || ad?.headline || 'Write the ad copy to see it here.'}</p>
                    {renderMedia()}
                    {ad && (ad.headline || ad.description || ad.cta) && (
                        <div className="ls-cflow-ad-foot">
                            <div className="ls-cflow-ad-copy">
                                {ad.headline && <span className="ls-cflow-ad-headline">{ad.headline}</span>}
                                {ad.description && <span className="ls-cflow-ad-description">{ad.description}</span>}
                            </div>
                            {ad.cta && <span className="ls-cflow-ad-cta">{ad.cta}</span>}
                        </div>
                    )}
                </article>
            );
        }

        const body = flow.caption.trim();
        const opener = flow.hook.trim();
        const showHook = opener && !body.toLowerCase().startsWith(opener.toLowerCase());
        return (
            <article className="ls-cflow-post">
                {head(`${platformLabel(platform)}${kind === 'video' ? ' · Video' : ''}`)}
                {renderMedia()}
                <div className="ls-cflow-post-text">
                    {showHook && <p className="ls-cflow-post-hook">{opener}</p>}
                    {body ? <p>{body}</p> : !showHook && <p className="ls-cflow-post-empty">Pick a hook and write a caption to see your post here.</p>}
                    {flow.link && platform !== 'instagram' && platform !== 'tiktok' && <p className="ls-cflow-post-link">{flow.link}</p>}
                    {flow.hashtags.length > 0 && <p className="ls-cflow-post-tags">{flow.hashtags.join(' ')}</p>}
                </div>
            </article>
        );
    };

    const count = isPost ? postText.length : null;
    const editor = renderEditor();

    const renderActions = () => {
        const download = mediaSrc ? (
            <a
                className="ls-btn ls-btn-secondary is-block"
                href={mediaSrc}
                download={flow.video ? 'video.mp4' : `image.${extensionOf(flow.image)}`}
            >
                <Icon name="download" size={18} />Download {flow.video ? 'video' : kind === 'blog' ? 'cover' : kind === 'ads' ? 'creative' : 'image'}
            </a>
        ) : null;
        const copyButton = (primary) => (
            <button type="button" className={`ls-btn ${primary ? 'ls-btn-primary' : 'ls-btn-secondary'} is-block`} onClick={copy} disabled={!copyText}>
                <Icon name={copied ? 'check' : 'content_copy'} size={18} />
                {copied ? 'Copied' : kind === 'blog' ? 'Copy article' : kind === 'ads' ? 'Copy ad copy' : 'Copy post'}
            </button>
        );
        const publishButton = isPost ? (
            <button type="button" className="ls-btn ls-btn-secondary is-block" onClick={openPublish} disabled={preparing || !(postText || mediaSrc)}>
                {preparing ? <span className="ls-spinner" /> : <Icon name="send" size={18} />}Publish…
            </button>
        ) : null;

        return (
            <div className="ls-card ls-cflow-finish">
                <div>
                    <div className="ls-card-title">{planPost ? 'Finish the planned post' : 'Post it'}</div>
                    <div className="ls-card-sub">
                        {planPost
                            ? `${[planPost.day, planPost.time, planPost.platform].filter(Boolean).join(' · ') || 'On your content plan'}${planPost.theme ? ` · ${planPost.theme}` : ''}`
                            : isPost ? 'Copy it, download the media, or publish to your connected accounts.' : 'Copy it and download the media.'}
                    </div>
                </div>
                <div className="ls-cflow-finish-actions">
                    {planPost ? (
                        <>
                            <button type="button" className="ls-btn ls-btn-primary is-block" onClick={saveToPlan} disabled={!draft || saving || isSaved}>
                                {saving ? <span className="ls-spinner" /> : <Icon name={isSaved ? 'check' : 'event_available'} size={18} />}
                                {isSaved ? 'Saved to plan' : savedAs ? 'Save changes to plan' : 'Save to plan'}
                            </button>
                            {publishButton}
                            {copyButton(false)}
                            {download}
                            <button type="button" className="ls-btn ls-btn-secondary is-block" onClick={markPosted} disabled={marking || Boolean(posted)}>
                                {marking ? <span className="ls-spinner" /> : <Icon name="task_alt" size={18} />}
                                {posted ? 'Posted' : 'Mark as posted'}
                            </button>
                        </>
                    ) : (
                        <>
                            {copyButton(true)}
                            {publishButton}
                            {download}
                        </>
                    )}
                </div>
                <div ref={statusRef} className="ls-cflow-status">
                    {status && (
                        <Alert severity={status.severity} onClose={() => setStatus(null)}>
                            {status.text}
                        </Alert>
                    )}
                </div>
                <div className="ls-cflow-finish-links">
                    {planPost && (
                        <button type="button" className="ls-link" onClick={backToPlan}>
                            <Icon name="arrow_back" size={16} />Back to plan
                        </button>
                    )}
                    <button type="button" className="ls-link" onClick={startOver}>
                        <Icon name="restart_alt" size={16} />Start a new post
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="ls-cflow-review">
            {missing.length > 0 && (
                <Alert severity="info">
                    <span className="ls-cflow-missing">
                        Still to do:
                        {missing.map(step => (
                            <button key={step.id} type="button" className="ls-link" onClick={() => goTo(step.id, { focus: true })}>
                                {step.label}
                            </button>
                        ))}
                        <span>You can also write it here.</span>
                    </span>
                </Alert>
            )}
            {/* Preview beside the editor and actions; on phones: preview, actions, then the editor */}
            <div className="ls-cflow-review-grid">
                <div className="ls-card ls-cflow-area-preview">
                    <div className="ls-card-header">
                        <div>
                            <div className="ls-card-title">Preview</div>
                            <div className="ls-card-sub">
                                {kind === 'blog' ? 'How the article shows as a link preview.' : kind === 'ads' ? 'Roughly how the ad shows in the feed.' : 'Roughly how it shows in the feed.'}
                            </div>
                        </div>
                        {count !== null && (
                            <span className={`ls-cflow-count${count > limit ? ' is-over' : ''}`} title={`${platformLabel(platform)} takes up to ${limit.toLocaleString()} characters`}>
                                {count.toLocaleString()} / {limit.toLocaleString()}
                            </span>
                        )}
                    </div>
                    {renderPreview()}
                    {count !== null && count > limit && (
                        <div className="ls-cflow-over" role="status">
                            {platformLabel(platform)} takes up to {limit.toLocaleString()} characters. Shorten it by {(count - limit).toLocaleString()}.
                        </div>
                    )}
                    {!mediaSrc && mediaStep && (
                        <div className="ls-cflow-inline">
                            <span className="ls-hint">No {mediaStep.label.toLowerCase()} yet.</span>
                            <button type="button" className="ls-link" onClick={() => goTo(mediaStep.id, { focus: true })}>Add one</button>
                        </div>
                    )}
                </div>
                <div className="ls-cflow-area-actions">
                    {renderActions()}
                </div>
                {editor && (
                    <div className="ls-card ls-cflow-area-edit">
                        <div>
                            <div className="ls-card-title">Edit</div>
                            <div className="ls-card-sub">Changes show in the preview and in the earlier steps.</div>
                        </div>
                        <div className="ls-form">{editor}</div>
                    </div>
                )}
            </div>

            {publish && (
                <PublishModal
                    post={planPost || { platform: platformLabel(platform), theme: flow.hook || 'New post', hook: flow.hook }}
                    weekStart={planPost ? seed.planWeek : ''}
                    linkDestination={planPost ? readLinkDestination() : ''}
                    initialText={postText}
                    initialFiles={publish.files}
                    onClose={() => setPublish(null)}
                    onPublished={handlePublished}
                />
            )}
        </div>
    );
};

export default ReviewStep;
